import React, { memo } from 'react';
import { Box, Typography } from '@mui/material';
import type { MetricsBlock, Metric } from '../../../types/backend';
import { TREND_GLYPH } from './blockFormat';
import BlockShell from './BlockShell';

interface Props {
  block: MetricsBlock;
}

const TREND_COLOR: Record<NonNullable<Metric['trend']>, string> = {
  up: 'success.main',
  down: 'error.main',
  flat: 'text.secondary',
};

const MetricCard: React.FC<{ metric: Metric }> = ({ metric }) => (
  <Box
    sx={{
      flex: '1 1 140px',
      minWidth: 120,
      p: 1.5,
      borderRadius: 1.5,
      bgcolor: 'action.hover',
      border: '1px solid',
      borderColor: 'divider',
    }}
  >
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
      {metric.label}
    </Typography>
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
      <Typography
        variant="h5"
        sx={{ fontWeight: 700, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}
      >
        {metric.value}
      </Typography>
      {metric.unit && (
        <Typography variant="caption" color="text.secondary">
          {metric.unit}
        </Typography>
      )}
      {metric.trend && (
        <Typography
          component="span"
          aria-label={`trend ${metric.trend}`}
          sx={{ ml: 0.5, color: TREND_COLOR[metric.trend], fontWeight: 700, lineHeight: 1 }}
        >
          {TREND_GLYPH[metric.trend]}
        </Typography>
      )}
    </Box>
    {metric.note && (
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, display: 'block' }}>
        {metric.note}
      </Typography>
    )}
  </Box>
);

const MetricsBlockView: React.FC<Props> = memo(({ block }) => (
  <BlockShell bodyPadding={0.5}>
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
      {block.metrics.map((metric, i) => (
        <MetricCard key={i} metric={metric} />
      ))}
    </Box>
  </BlockShell>
));

MetricsBlockView.displayName = 'MetricsBlockView';
export default MetricsBlockView;
